import { Dispatch, ReactNode, SetStateAction, useMemo } from "react";

import { SelectPicker } from "rsuite";
import { Field, Module } from "../../../types/TenantInterfaces";
import ModuleFieldsPicker from "../ModuleFieldsPicker";
import RequiredTitle from "../../../elements/RequiredTitle";
import { DrawerError } from "../../Drawers/AppDrawer";

export type CardTypes = "Default" | "Image" | "Folder";

interface Props {
  module: Module;
  availableFields: Field[];
  fieldSchema: any;
  layoutLimit?: number;
  setError: Dispatch<SetStateAction<DrawerError | null>>;
  moduleFieldsChange: (fields: any) => void;
  frontFields: string[];
  setFrontFields: React.Dispatch<React.SetStateAction<string[]>>;
  backFields: string[];
  setBackFields: React.Dispatch<React.SetStateAction<string[]>>;
  cardType: CardTypes;
  setCardType: React.Dispatch<React.SetStateAction<CardTypes>>;
  cardImage: string;
  setCardImage: React.Dispatch<React.SetStateAction<string>>;
}

const empty: string[] = [];

const CardsLayoutEditor = ({
  module,
  availableFields,
  fieldSchema,
  setError,
  frontFields,
  setFrontFields,
  backFields,
  setBackFields,
  cardType,
  setCardType,
  cardImage,
  setCardImage,
}: Props) => {
  // TODO: Add support for Back fields
  // const backAvailableFields = useMemo(
  //   () => availableFields.filter((f) => !frontFields?.includes(f.Name)),
  //   [frontFields]
  // );

  const availableImageFields = useMemo(() => {
    return (
      module?.Fields?.filter(
        (f) => f.FieldType === "Image" || f.FieldType === "SingleFile"
      ) || []
    );
  }, [module?.Fields]);

  return (
    <>
      <h4>Card Type</h4>
      <div className="d-flex w-100 flex-wrap">
        <CardTypeOption
          checked={cardType === "Default" || !cardType}
          onChange={() => {
            handleCardTypeChange("Default");
            moduleFieldsChange(empty, "Fields");
          }}
          CardComponent={<DefaultCard />}
          title="Default Card"
          className="mr-5"
        />
        {/* <CardTypeOption
          checked={cardType === "Business"}
          onChange={() => handleCardTypeChange("Business")}
          CardComponent={<BuisinessCard />}
          title="Business"
        /> */}
        <CardTypeOption
          checked={cardType === "Image"}
          onChange={() => {
            handleCardTypeChange("Image");
            moduleFieldsChange(empty, "Fields");
          }}
          CardComponent={<ImageCard />}
          title="Image Card"
        />
        <CardTypeOption
          checked={cardType === "Folder"}
          onChange={() => {
            moduleFieldsChange(["LastUpdated", "Tags"], "Fields");
            handleCardTypeChange("Folder");
          }}
          CardComponent={<FolderCard />}
          title="Folder"
        />
      </div>

      {cardType !== "Folder" && (
        <>
          <RequiredTitle title={`Select Front Fields`} size="md" />
          <ModuleFieldsPicker
            module={module}
            disabled={!cardType}
            availableFields={availableFields}
            values={frontFields}
            setValues={setFrontFields}
            fieldSchema={fieldSchema}
            layoutLimit={cardType === "Image" ? 3 : 5}
            setError={setError}
            onChange={(fields: string[]) => {
              moduleFieldsChange(fields, "Fields");
              console.log("fields", fields);
            }}
            placeholder={"Select Fields"}
            // onClear={() => setError(null)}
          />
        </>
      )}

      {/* TODO: Add Back Field Support */}
      {/* <div className="mt-3">
        <h4>Select Back Fields</h4>
        <ModuleFieldsPicker
          module={module}
          availableFields={backAvailableFields}
          values={backFields}
          setValues={setBackFields}
          fieldSchema={fieldSchema}
          layoutLimit={5}
          disabled={!cardType}
          setError={setError}
          onChange={(fields: string[]) =>
            moduleFieldsChange(fields, "BackFields")
          }
          placeholder={"Select Fields"}
        />
      </div> */}

      <div
        className="mt-4 mb-6"
        style={{ opacity: ["Image", "Folder"].includes(cardType) ? 1 : 0 }}
      >
        <RequiredTitle
          title={cardType === "Image" ? "Card Image " : "Folder Image"}
          size="md"
        />
        <SelectPicker
          block
          labelKey="Name"
          valueKey="APIName"
          placeholder="Select Image Field for Cards"
          preventOverflow
          data={availableImageFields}
          value={cardImage}
          onChange={(value: string | null) => setCardImage(value || "")}
          disabled={["Image", "Folder"].includes(cardType) ? false : true}
        />
      </div>
    </>
  );

  function moduleFieldsChange(
    fields: any,
    key: "Fields" | "BackFields" = "Fields"
  ) {
    const limit = cardType === "Image" && key === "Fields" ? 3 : 5;

    if (fields.length > limit) {
      return setError({
        message: `You can only add ${limit} fields`,
        onClear: () => setError(null),
      });
    }

    if (key === "Fields") {
      return setFrontFields(fields);
    }
    if (key === "BackFields") {
      return setBackFields(fields);
    }
  }

  function handleCardTypeChange(value: CardTypes) {
    if (frontFields.length > 5 && ["Default", "Business"].includes(value)) {
      return setError({
        message: `You can only add 5 fields`,
        onClear: () => setError(null),
      });
    }
    if (frontFields.length > 3 && value === "Image") {
      return setError({
        message: `You can only add 3 fields`,
        onClear: () => setError(null),
      });
    }

    setCardType(value);
  }
};

function CardTypeOption({
  checked,
  onChange,
  CardComponent,
  title,
  className,
}: {
  checked: boolean;
  onChange: () => void;
  CardComponent: ReactNode;
  title: string;
  className?: string;
}) {
  return (
    <div className={`mb-3 ${className || ""}`}>
      <div className="d-flex align-items-center py-2">
        <input
          className="mr-1"
          type="radio"
          checked={checked}
          onChange={onChange}
        />
        <div onClick={onChange} className="cursor-pointer">
          {CardComponent}
        </div>
      </div>
      <div
        className={`text-center font-weight-500 ${
          checked ? "text-primary" : ""
        }`}
      >
        {title}
      </div>
    </div>
  );
}

const DefaultCard = () => (
  <svg
    width="211"
    height="117"
    viewBox="0 0 211 117"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="211" height="117" rx="4" fill="#BDC9C2" />
    <circle cx="14" cy="14" r="6" fill="#E8EDEA" />
    <rect x="28" y="8" width="175" height="12" rx="4" fill="#E8EDEA" />
    <rect x="8" y="28" width="195" height="15.3333" rx="4" fill="#E8EDEA" />
    <rect
      x="8"
      y="59.3334"
      width="195"
      height="15.3333"
      rx="4"
      fill="#E8EDEA"
    />
    <rect
      x="8"
      y="90.6666"
      width="195"
      height="15.3333"
      rx="4"
      fill="#E8EDEA"
    />
  </svg>
);

const BuisinessCard = () => (
  <svg
    width="211"
    height="117"
    viewBox="0 0 211 117"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="211" height="117" rx="4" fill="#BDC9C2" />
    <circle cx="41.5" cy="41.5" r="33.5" fill="#E8EDEA" />
    <rect x="83" y="8" width="120" height="23" rx="4" fill="#E8EDEA" />
    <rect x="83" y="47" width="120" height="23" rx="4" fill="#E8EDEA" />
    <rect x="83" y="86" width="120" height="23" rx="4" fill="#E8EDEA" />
  </svg>
);

const ImageCard = () => (
  <svg
    width="211"
    height="117"
    viewBox="0 0 211 117"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect width="211" height="117" rx="4" fill="#BDC9C2" />
    <rect x="8" y="8" width="195" height="78" rx="4" fill="#E8EDEA" />
    <rect x="8" y="94" width="195" height="12" rx="4" fill="#E8EDEA" />
  </svg>
);

const FolderCard = () => {
  return (
    <div className="d-flex flex-column">
      <svg
        width="211"
        height="14"
        viewBox="0 0 211 14"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        style={{ marginBottom: -3 }}
      >
        <path
          d="M0 4C0 1.79086 1.79086 0 4 0H49.331C49.9498 0 50.5601 0.143542 51.114 0.419337L77.8859 14H0V4Z"
          fill="#BDC9C2"
        />
      </svg>

      <svg
        width="211"
        height="117"
        viewBox="0 0 211 117"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <rect width="211" height="117" rx="4" fill="#BDC9C2" />
        <rect x="8" y="8" width="195" height="12" rx="4" fill="#E8EDEA" />
        <rect x="8" y="27" width="195" height="60" rx="4" fill="#E8EDEA" />
        <rect x="8" y="94" width="195" height="12" rx="4" fill="#E8EDEA" />
      </svg>
    </div>
  );
};

export default CardsLayoutEditor;
